import { Authorization, User } from "@/types/proto/exabel/user/user_messages";

export enum Permission {
  ACCESS_EXPERIMENTAL = "access:experimental",
  ACCESS_IMPORT_TIME_SERIES = "access:import_timeseries",
  ACCESS_MANAGE_DATA_SET = "access:manage_data_set",
  ACCESS_SCREENER = "access:screener",
  ACCESS_SUPPORT = "access:support",
  ACCESS_DATA_CATALOG = "access:data_catalog",
  ACCESS_EXABEL_LANDING_PAGE = "access:exabel_landing_page",
  ACCESS_PLOTTER = "access:plotter",
  ACCESS_LEGACY_MODELS = "access:legacy_models",
  ACCESS_DATA = "access:data",
  ACCESS_JOBS = "access:jobs",
  ACCESS_HIERARCHICAL_MODELS = "access:hierarchical_models",
  ACCESS_KPI_COMPARISON = "access:kpi_comparison",
  EXPORT_DASHBOARD = "export:dashboard",
  EXPORT_FINANCIAL_MODEL = "export:financial_models",
  EXPORT_SIGNALS = "export:signals",
  EXPORT_SIGNALS_LIMITED = "export:signals_limited",
  READ_DEBUG = "read:debug",
  READ_EXABEL_APIS = "read:exabel_apis",
  READ_IMPORT_JOBS = "read:import_jobs",
  READ_PUBLIC_KPI = "read:public_kpi",
  READ_WIDGET_QUEUE = "read:widget_queue",
  READ_ADC_AUDIT = "read:adc_audit",
  READ_USER = "read:users",
  WRITE_ALERT = "write:alerts",
  WRITE_ALPHA_ANALYSIS = "write:alpha_analysis",
  WRITE_CHART = "write:charts",
  WRITE_COMPANY_DASHBOARD = "write:company_dashboard",
  WRITE_CONNECTION = "write:connection",
  WRITE_CUSTOMER_WATCHLIST = "write:customer_watchlist",
  WRITE_DASHBOARD = "write:dashboards",
  WRITE_FINANCIAL_MODEL = "write:financial_models",
  WRITE_IMPORT_JOBS = "write:import_jobs",
  WRITE_MODEL = "write:model",
  WRITE_PORTFOLIO_STRATEGY = "write:portfolio_strategy",
  WRITE_PUBLIC_SHARING = "write:public_sharing",
  WRITE_USERS = "write:users",
  WRITE_VENDOR_ASSETS = "write:vendor_assets",
  WRITE_KPI = "write:kpi",
  WRITE_KPI_HIERARCHY = "write:kpi_hierarchy",
  WRITE_PUBLIC_KPI = "write:public_kpi",
  WRITE_DERIVED_SIGNAL = "write:derived_signal",
}

enum Role {
  INSIGHTS_PLATFORM = "insights_platform",
  INSIGHTS_PLUS = "insights_platform_plus",
  FULL_PLATFORM = "full_platform",
  EXABEL_EMPLOYEE = "exabel_employee",
}

export enum SubscriptionPlan {
  EXABEL_EMPLOYEE = 4,
  FULL_PLATFORM = 3,
  INSIGHTS_PLUS = 2,
  INSIGHTS = 1,
  UNKNOWN = 0,
}

const hasRole = (auth: Authorization, role: Role): boolean =>
  auth?.roles?.includes(role) ?? false;

// Check if the auth permissions list contains a permission.
export const hasPermission = (user: User) => (permission: Permission) => {
  return Boolean(
    user?.authorization?.permissions &&
    user.authorization.permissions.includes(permission)
  );
};

// Note: a user may have multiple roles, so we pick the "highest" level plan.
// E.g. they may get FULL_PLATFORM from their main Exabel subscription,
// and INSIGHTS_PLATFORM from their Insights Platform subscription.
export const getSubscriptionPlan = (auth: Authorization): SubscriptionPlan => {
  if (hasRole(auth, Role.EXABEL_EMPLOYEE))
    return SubscriptionPlan.EXABEL_EMPLOYEE;
  if (hasRole(auth, Role.FULL_PLATFORM)) return SubscriptionPlan.FULL_PLATFORM;
  if (hasRole(auth, Role.INSIGHTS_PLUS)) return SubscriptionPlan.INSIGHTS_PLUS;
  if (hasRole(auth, Role.INSIGHTS_PLATFORM)) return SubscriptionPlan.INSIGHTS;
  return SubscriptionPlan.UNKNOWN;
};

// Check if the user is expired
export const isExpired = (user: User, date = new Date()) =>
  !!user.subscriptionEnd && new Date(user.subscriptionEnd) < date;
